<template>
  <Layout>
    <PageHeader :title="title" :items="breadCrumbs" />
    <div class="wallet-container">
      <b-card>
        <div class="section-title mb-3">Select Payment Type</div>
        <b-row class="mb-4">
          <b-col cols="3" v-for="(pm, index) in paymentMethodList" :key="index">
            <div
              :class="{
                active:
                  selectedPm != null && selectedPm.methodId == pm.methodId,
              }"
              class="d-flex flex-column payment-method-item mb-3"
              @click="setPaymentMethod(pm)"
            >
              <div class="mb-2">
                <img class="pm-logo" :src="pm.logo" />
              </div>
              <div class="mb-1 pm-text">
                {{ pm.displayName }}
              </div>
              <div class="ts-8 pm-details">
                {{
                  pm.name == "Bank Transfer"
                    ? "1-2 working days processing"
                    : "Real time processing"
                }}
              </div>
            </div>
          </b-col>
        </b-row>

        <div v-if="selectedPm != null && selectedPm.name != 'Bank Transfer'">
          <div class="section-title mb-3">Select Amount</div>
          <b-row class="mb-4">
            <b-col
              class="mb-3"
              cols="4"
              v-for="(sku, index) in walletSkus"
              :key="index"
            >
              <div
                :class="{
                  active: selectedSku != null && selectedSku.skuId == sku.skuId,
                  disabled: sku.amount < selectedPm.minAmount,
                }"
                class="d-flex text-center align-items-center justify-content-center sku-item"
                @click="setSelectedSku(sku)"
              >
                <div>{{ sku.displayName }}</div>
              </div>
            </b-col>
          </b-row>
        </div>

        <div v-if="selectedPm && selectedPm.name != 'Bank Transfer'">
          <b-form
            ref="otherAmountForm"
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="reset"
          >
            <b-form-group class="mb-4">
              <label>Enter Amount</label>
              <validation-provider
                name="topup amount"
                :rules="amountValidation"
                v-slot="validationContext"
              >
                <b-form-input
                  :state="getValidationState(validationContext)"
                  placeholder="Enter amount"
                  type="number"
                  v-model="selectedAmount"
                  trim
                ></b-form-input>
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </validation-provider>
            </b-form-group>
          </b-form>
        </div>

        <div v-if="selectedPm && selectedPm.name == 'Card'">
          <div class="section-title mb-3">Payment Details</div>
          <ValidationObserver ref="cardDetail" v-slot="{ handleSubmit, reset }">
            <b-form
              ref="creditCardForm"
              @submit.prevent="handleSubmit(onSubmit)"
              @reset.prevent="reset"
            >
              <b-form-group class="mb-4">
                <label>Card Number <span class="red">*</span></label>
                <validation-provider
                  name="card number"
                  rules="required|min:16|numeric"
                  v-slot="validationContext"
                >
                  <b-form-input
                    :state="getValidationState(validationContext)"
                    placeholder="Enter card number"
                    maxlength="16"
                    oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');"
                    v-model="cardDetails.cardNumber"
                    trim
                  ></b-form-input>
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </validation-provider>
              </b-form-group>

              <b-row>
                <b-col>
                  <b-form-group class="mb-4">
                    <label
                      >Card Holder's Full Name <span class="red">*</span></label
                    >
                    <validation-provider
                      name="card name"
                      rules="required"
                      v-slot="validationContext"
                    >
                      <b-form-input
                        :state="getValidationState(validationContext)"
                        placeholder="Enter cardholder name"
                        v-model="cardDetails.cardName"
                        trim
                      ></b-form-input>
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group class="mb-4">
                    <label>Email <span class="red">*</span></label>
                    <validation-provider
                      name="card email"
                      rules="required|email"
                      v-slot="validationContext"
                    >
                      <b-form-input
                        :state="getValidationState(validationContext)"
                        placeholder="Enter email"
                        v-model="cardDetails.cardEmail"
                        trim
                      ></b-form-input>
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>

              <div class="section-title mb-3">Expiration Date</div>

              <b-row>
                <b-col>
                  <b-form-group class="mb-4">
                    <label>Month <span class="red">*</span></label>
                    <validation-provider
                      name="card month"
                      rules="required"
                      v-slot="validationContext"
                    >
                      <b-form-select
                        class="form-control"
                        :state="getValidationState(validationContext)"
                        v-model="cardDetails.cardMonth"
                        :options="months"
                        trim
                      ></b-form-select>
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group class="mb-4">
                    <label>Year <span class="red">*</span></label>
                    <validation-provider
                      name="card year"
                      rules="required"
                      v-slot="validationContext"
                    >
                      <b-form-select
                        class="form-control"
                        :options="years"
                        :state="getValidationState(validationContext)"
                        v-model="cardDetails.cardYear"
                        trim
                      ></b-form-select>
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group class="mb-4">
                    <label>CVV <span class="red">*</span></label>
                    <validation-provider
                      name="cvv"
                      rules="required"
                      v-slot="validationContext"
                    >
                      <b-form-input
                        :state="getValidationState(validationContext)"
                        placeholder=""
                        maxlength="3"
                        v-model="cardDetails.cardCvv"
                        trim
                      ></b-form-input>
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-form>
          </ValidationObserver>
        </div>

        <div v-if="selectedPm && selectedPm.name == 'Bank Transfer'">
          <div class="section-title mb-4">Bank Details</div>
          <div class="text-primary ts-14 fw-500 mb-3">Instruction:</div>
          <ol class="ps-3 mb-4">
            <li>
              Select bank of choice to receive details.
              <div class="d-inline-block">
                <div @click="showGuide('step1')" class="ts-10 ms-1 d-flex align-items-center text-primary cursor-pointer">
                  <span class="material-icons-outlined md-18 ts-18 me-1"
                    >info</span
                  >
                  <div><u>View Guide</u></div>
                </div>    
              </div>
            </li>
            <li>
              Process the transfer and obtain the proof of payment along with a screenshot.
              <div class="d-inline-block">
                <div @click="showGuide('step2')" class="ts-10 ms-1 d-flex align-items-center text-primary cursor-pointer">
                  <span class="material-icons-outlined md-18 ts-18 me-1"
                    >info</span
                  >
                  <div><u>View Guide</u></div>
                </div>
              </div>
            </li>
            <li>
              Input transaction details and Upload Screenshot.
              <div class="d-inline-block">
                <div @click="showGuide('step3')" class="ts-10 ms-1 d-flex align-items-center text-primary cursor-pointer">
                  <span class="material-icons-outlined md-18 ts-18 me-1"
                    >info</span
                  >
                  <div><u>View Guide</u></div>
                </div>
              </div>
            </li>
            <li>
              Your top-up will be processed in 1-2 working days. Kindly wait for
              Email notification once confirmed.
            </li>
          </ol>

          <ValidationObserver ref="bankTrans" v-slot="{ handleSubmit, reset }">
            <b-form
              ref="bankTransferForm"
              @submit.prevent="handleSubmit(onSubmit)"
              @reset.prevent="reset"
            >
              <b-form-group class="mb-3">
                <label>Choose Bank: <span class="red">*</span></label>
                <validation-provider
                  name="bank"
                  rules="required"
                  v-slot="validationContext"
                >
                  <b-form-select
                    :state="getValidationState(validationContext)"
                    :options="bankOptions"
                    v-model="bankTransferDetails.bankTransferTo"
                    class="form-select"
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </validation-provider>
              </b-form-group>

              <b-row class="mb-2" v-if="selectedBank.accountName">
                <b-col>
                  <div class="d-flex ms-3">
                    <label class="mb-0">Account Name: {{ selectedBank.accountName }}</label>
                    <div
                      @click="copyToClipboard(selectedBank.accountName)"
                      class="ms-3 d-flex text-primary cursor-pointer"
                      v-if="selectedBank.accountName"
                    >
                      <span class="material-icons md-18 ts-18 me-1"
                        >content_copy</span
                      >
                      <div>Copy</div>
                    </div>
                  </div>
                </b-col>
              </b-row>
              <b-row class="mb-3" v-if="selectedBank.accountNumber">
                <b-col>
                  <div class="d-flex ms-3">
                    <label
                     class="mb-0"
                      >Account Number: {{ selectedBank.accountNumber }}</label
                    >
                    <div
                      @click="copyToClipboard(selectedBank.accountNumber)"
                      class="ms-3 d-flex text-primary cursor-pointer"
                      v-if="selectedBank.accountNumber"
                    >
                      <span class="material-icons md-18 ts-18 me-1"
                        >content_copy</span
                      >
                      <div>Copy</div>
                    </div>
                  </div>
                </b-col>
              </b-row>

              <b-form-group class="mb-3">
                <label>Top-up Amount: <span class="red">*</span></label>
                <validation-provider
                  name="topup amount"
                  :rules="amountValidation"
                  v-slot="validationContext"
                >
                  <b-form-input
                    :state="getValidationState(validationContext)"
                    placeholder="Enter amount"
                    type="number"
                    v-model="selectedAmount"
                    trim
                  ></b-form-input>
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </validation-provider>
              </b-form-group>

              <b-row>
                <b-col cols="12">
                  <b-form-group class="mb-4">
                    <label>Reference Number <span class="red">*</span></label>
                    <validation-provider
                      name="reference number"
                      rules="required"
                      v-slot="validationContext"
                    >
                      <b-form-input
                        :state="getValidationState(validationContext)"
                        placeholder="ex. 2124674343"
                        v-model="bankTransferDetails.referenceNumber"
                        trim
                      ></b-form-input>
                      <b-form-invalid-feedback>
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>

              <div class="mb-3">
                Proof of Payment <span class="red">*</span>
              </div>
              <b-row>
                <b-col>
                  <image-uploader
                    :isRequired="true"
                    uploadText="Upload proof of payment receipt for the bank transfer."
                    class="me-4"
                    v-model="bankTransferDetails.proofOfPayment"
                    :showDeleteButton="true"
                  />
                </b-col>
              </b-row>
            </b-form>
          </ValidationObserver>
        </div>

        <div class="d-flex justify-content-between">
          <div class="d-flex align-items-center">
            <template v-if="hasReferrer">
              <div
                class="d-flex cursor-pointer"
                @click="
                  () => {
                    $router.push({ name: 'confirm-order' });
                  }
                "
              >
                <span class="text-primary material-icons md-18 ts-19 me-1"
                  >chevron_left</span
                >
                <div>Back to Checkout</div>
              </div>
            </template>
          </div>
          <div>
            <b-button
              class="next-button"
              variant="primary"
              @click="previewTopup"
              :disabled="
                isLoading ||
                !(selectedPm && selectedAmount >= selectedPm.minAmount)
              "
            >
              <span
                v-if="isLoading"
                class="spinner-grow spinner-grow-sm"
                role="status"
                aria-hidden="true"
              ></span>
              {{
                selectedPm && selectedPm.name == "Bank Transfer"
                  ? "Request"
                  : "Next"
              }}
            </b-button>
          </div>
        </div>
        <div v-if="false">
          <img src="@/assets/images/info.png" class="me-2" />Acknowledgment
          receipts are only issued for top-ups.
        </div>
      </b-card>
    </div>

    <!-- Preview modal -->
    <b-modal
      id="preview-modal"
      centered
      v-model="isPreviewModal"
      ok-title="Confirm"
      @ok="proceedPayment"
      :ok-disabled="isLoading || !hasAcceptAgreement"
    >
      <div class="text-center mb-4">
        <img src="@/assets/images/orange-wallet.png" class="mb-2" />
        <div class="modal-title">Top-Up Review</div>
      </div>
      <div class="d-flex justify-content-between mb-2">
        <div class="modal-label">Payment Type</div>
        <div v-if="selectedPm">{{ selectedPm.displayName }}</div>
      </div>
      <div v-if="selectedPm && selectedPm.name == 'Card'">
        <div class="d-flex justify-content-between mb-2">
          <div class="modal-label">Card Holder's Full Name</div>
          <div>{{ cardDetails.cardName }}</div>
        </div>
        <div class="d-flex justify-content-between mb-2">
          <div class="modal-label">Card Number</div>
          <div v-if="cardDetails.cardNumber">
            **** **** ****
            {{
              cardDetails.cardNumber.substr(cardDetails.cardNumber.length - 4)
            }}
          </div>
        </div>
        <div class="d-flex justify-content-between mb-2">
          <div class="modal-label">Expiration Number</div>
          <div>{{ cardMonth + " " + cardDetails.cardYear }}</div>
        </div>
      </div>
      <div class="d-flex justify-content-between mb-2">
        <div class="modal-label">Top-Up Amount</div>
        <div v-if="selectedAmount">PHP {{ amountToBeReceived }}</div>
      </div>
      <hr />
      <div class="d-flex justify-content-between mb-4">
        <div class="modal-label">Amount to be received</div>
        <div v-if="selectedAmount">PHP {{ amountToBeReceived }}</div>
      </div>

      <div class="form-check" v-if="false">
        <input
          v-model="hasAcceptAgreement"
          type="checkbox"
          class="form-check-input"
        />
        <label class="form-check-label ts-12"
          >Please confirm that you agree to our
          <a class="text-primary">Terms and Conditions</a></label
        >
      </div>

      <template #modal-ok>
        <span
          v-if="isLoading"
          class="spinner-grow spinner-grow-sm"
          role="status"
          aria-hidden="true"
        ></span>
        Confirm
      </template>
    </b-modal>

    <!-- Receipt modal -->
    <b-modal
      id="receipt-modal"
      centered
      v-model="isReceiptModal"
      ok-only
      ok-title="Confirm"
      @ok="reset"
      @close="reset"
    >
      <div class="text-center mb-4">
        <img
          v-if="paymentStatus == 'success'"
          src="@/assets/images/success.png"
          class="mb-2"
        />
        <img
          v-if="paymentStatus == 'pending'"
          src="@/assets/images/clock.png"
          class="mb-2"
        />
        <div
          v-if="paymentStatus == 'success'"
          class="modal-title payment-status"
        >
          Top-Up Success
        </div>
        <div
          v-if="paymentStatus == 'pending'"
          class="modal-title payment-status"
        >
          Pending Top-Up
        </div>

        <div v-if="paymentStatus == 'success' && false">
          The acknowledgement receipt has been sent to<br /><span
            class="green"
            >{{ this.currentUser.email }}</span
          >
        </div>
        <div v-if="paymentStatus == 'pending'">
          The top-up is currently pending and being processed. Please check your
          Wallet History for updates.
        </div>
      </div>
      <div class="d-flex justify-content-between mb-2">
        <div class="modal-label">Payment Date</div>
        <div>{{ paymentDate }}</div>
      </div>
      <div class="d-flex justify-content-between mb-2">
        <div class="modal-label">Payment Status</div>
        <div class="green payment-status">{{ paymentStatus }}</div>
      </div>
      <hr class="hr-broken" />
      <div class="d-flex justify-content-between mb-2">
        <div class="modal-label">Payment Type</div>
        <div v-if="selectedPm">{{ selectedPm.displayName }}</div>
      </div>
      <div v-if="selectedPm && selectedPm.name == 'Card'">
        <div class="d-flex justify-content-between mb-2">
          <div class="modal-label">Card Number</div>
          <div v-if="cardDetails.cardNumber">
            **** **** ****
            {{
              cardDetails.cardNumber.substr(cardDetails.cardNumber.length - 4)
            }}
          </div>
        </div>
        <div class="d-flex justify-content-between mb-2">
          <div class="modal-label">Card Holder's Full Name</div>
          <div>{{ cardDetails.cardName }}</div>
        </div>
      </div>
      <div class="d-flex justify-content-between mb-2">
        <div class="modal-label">Top-Up Amount</div>
        <div>PHP {{ amountToBeReceived }}</div>
      </div>
      <hr class="hr-broken" />
      <div class="d-flex justify-content-between mb-2">
        <div class="modal-label">Amount To Be Received</div>
        <div>PHP {{ amountToBeReceived }}</div>
      </div>
      <hr class="hr-broken" />
      <div v-if="paymentStatus == 'success'" class="text-center mb-2 thank-you">
        Thank You!
      </div>
      <p v-if="paymentStatus == 'success'" class="text-center receipt-text">
        This is to acknowledge your top-up success. For OR requests, contact us
        via
        <a class="text-primary" href="mailto:kantoads@peddlr.io"
          >kantoads@peddlr.io</a
        >
        or message our Chat support.
      </p>
    </b-modal>

    <!-- Transfer modal -->
    <b-modal
      id="transfer-modal"
      centered
      v-model="isTransferModal"
      ok-only
      ok-title="Confirm"
      @ok="reset"
      @close="reset"
    >
      <div class="text-center">
        <img src="@/assets/images/clock.png" class="mb-2" />
        <div class="modal-title payment-status mb-3">
          Request is being processed
        </div>

        <div>
          Your request is being processed. Please allow<br />1-2 working days
          for processing. Once<br />approved, the changes will be
          automatically<br />
          reflected in your Wallet Balance.
        </div>
        <div v-if="paymentStatus == 'pending'">
          The top-up is currently pending and being processed. Please check your
          Wallet History for updates.
        </div>
      </div>
    </b-modal>

    <!-- Guide modal -->
    <b-modal
      id="guide-modal"
      centered
      v-model="isGuideModal"
      hide-footer
      body-class="p-1 mb-3"
    >
       <template v-if="guideStep == 'step1'">
        <div class="text-center fw-700 ts-16 mb-3">
          Choose bank of choice to receive details.
        </div>
        <img src="@/assets/images/step_1.png" class="mb-2 w-100" />
      </template>

      <div class="p-3" v-if="guideStep == 'step2'">
        <div class="text-center fw-700 ts-16 mb-3">
          Process the transfer and obtain the proof of payment along with a screenshot.
        </div>
        <img src="@/assets/images/step_2.png" class="mb-2 w-100" />
      </div>

      <template v-if="guideStep == 'step3'">
        <div class="text-center fw-700 ts-16 mb-3">
          Input transaction details and upload screenshot.
        </div>
        <img src="@/assets/images/step_3.png" class="mb-2 w-100" />
      </template>
    </b-modal>
  </Layout>
</template>

<script>
import {
  walletMethods,
  walletComputed,
  organizationMethods,
  authComputed,
} from "@/state/helpers";
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header-custom.vue";
import moment from "moment";
import ImageUploader from "@/components/ImageUploader.vue";

export default {
  data() {
    return {
      banks: [
        {
          name: "Bank of the Philippine Islands (BPI)",
          accountName: "PEDDLR PHILIPPINES INC",
          accountNumber: "9151002856",
        },
      ],
      isLoading: false,
      isGuideModal: false,
      guideStep: null,
      email: null,
      title: null,
      hasReferrer: false,
      hasAcceptAgreement: true,
      paymentDate: null,
      paymentStatus: null,
      isPreviewModal: false,
      isTransferModal: false,
      isReceiptModal: false,
      selectedPmId: null,
      selectedSkuId: null,
      selectedAmount: null,
      cardDetails: {
        cardNumber: null,
        cardName: null,
        cardEmail: null,
        cardMonth: null,
        cardYear: null,
        cardCvv: null,
      },
      bankTransferDetails: {
        bankTransferTo: null,
        referenceNumber: null,
        proofOfPayment: null,
        popKey: null,
      },
      months: [
        { text: "January", value: 1 },
        { text: "February", value: 2 },
        { text: "March", value: 3 },
        { text: "April", value: 4 },
        { text: "May", value: 5 },
        { text: "June", value: 6 },
        { text: "July", value: 7 },
        { text: "August", value: 8 },
        { text: "September", value: 9 },
        { text: "October", value: 10 },
        { text: "November", value: 11 },
        { text: "December", value: 12 },
      ],
    };
  },
  methods: {
    ...walletMethods,
    ...organizationMethods,
    showGuide(step){
      this.guideStep = step;
      this.isGuideModal = true;
    },
    copyToClipboard(text) {
      this.$copyText(text).then(
        () => {
          this.$toast.showToast("success", "Success!", "Copied to clipboard.");
        },
        () => {
          this.$toast.showToast(
            "danger",
            "Failed!",
            "Text has not been copied."
          );
        }
      );
    },
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null;
    },
    reset() {
      this.fetchWalletBalance();
      this.fetchHoldBalance();
      this.paymentDate = null;
      this.paymentStatus = null;
      this.selectedPmId = null;
      this.selectedSkuId = null;
      this.$router.replace({ query: null });
      this.bankTransferDetails = {
        bankTransferTo: null,
        referenceNumber: null,
        proofOfPayment: null,
        popKey: null,
      };

      if (localStorage.getItem("topupReferrer") !== null) {
        var topupReferrer = localStorage.getItem("topupReferrer");
        localStorage.removeItem("topupReferrer");

        this.$router.push({ name: topupReferrer, query: { success: 1 } });
      }
    },
    setPaymentMethod(mop) {
      this.selectedSkuId = null;
      this.selectedAmount = null;
      this.selectedPmId = mop.methodId;
    },
    setSelectedSku(sku) {
      this.selectedSkuId = sku.skuId;
      this.selectedAmount = sku.amount;
    },
    redirectToPayMessage(
      status,
      payModule,
      skuAmount,
      methodId,
      paymentIntentId
    ) {
      this.$router.push({
        name: "wallet-payment",
        params: {
          status: status,
          module: payModule,
          skuAmount: skuAmount,
          methodId: methodId,
        },
        query: {
          payment_intent_id: paymentIntentId,
        },
      });
    },
    async previewTopup() {
      if (this.selectedPm.name == "Card") {
        var result = await this.$refs.cardDetail.validate();
        if (result) {
          this.isPreviewModal = true;
        }
      } else if (this.selectedPm.name == "Bank Transfer") {
        var bankResult = await this.$refs.bankTrans.validate();
        if (bankResult) {
          this.isLoading = true;

          this.uploadProofOfPayment(this.bankTransferDetails.proofOfPayment)
            .then((key) => {
              var params = {
                bankName: this.bankTransferDetails.bankTransferTo,
                accountName: this.selectedBank.accountName,
                accountNumber: this.selectedBank.accountNumber,
                proofOfPayment: key,
                referenceNumber: this.bankTransferDetails.referenceNumber,
                amount: this.selectedAmount,
                module: "kanto_ads",
                paymentMethod: this.selectedPm.methodId,
              };

              console.log(params);
              this.createBankTransfer(params)
                .then((res) => {
                  console.log(res);
                  this.isLoading = false;

                  if (res.status == "ok") {
                    this.isTransferModal = true;
                  } else {
                    this.$toast.showToast(
                      "danger",
                      "Transaction Failed!",
                      "Unexpected error, please contact support."
                    );
                  }
                })
                .catch((err) => {
                  this.isLoading = false;
                  this.$toast.showToast(
                    "danger",
                    "Transaction Failed!",
                    "Unexpected error, please contact support."
                  );
                  console.log(err);
                });
            })
            .catch((err) => {
              console.log(err);
              this.isLoading = false;
              this.$toast.showToast("danger", "Upload Failed!", err);
            });
        }
      } else {
        this.isPreviewModal = true;
      }
    },
    proceedPayment(evt) {
      evt.preventDefault();

      var params = {
        module: "kanto_ads",
        amount: this.selectedAmount,
        paymentMethod: this.selectedPm.methodId,
        hasAcceptAgreement: this.hasAcceptAgreement,
      };

      if (this.selectedPm.name == "Card") {
        params.details = this.cardDetails;
      }

      this.isLoading = true;

      if (this.selectedPm.endpointType == "source") {
        this.paymodeOne(params)
          .then((res) => {
            if (res.status == "ok") {
              window.location.href =
                res.message.data.attributes.redirect.checkout_url;
            }
            this.isLoading = false;
            this.isPreviewModal = false;
          })
          .catch((err) => {
            this.isLoading = false;
            this.isPreviewModal = false;
            this.redirectToPayMessage(
              "failed",
              this.$route.query.module,
              this.$route.query.skuAmount,
              this.$route.query.methodId,
              this.$route.query.paymentIntentId
            );
            console.log(err);
          });
      } else {
        this.paymodeTwo(params)
          .then((res) => {
            if (res.status == "ok") {
              console.log(res);

              if (
                res.message.data.attributes.status == "awaiting_next_action"
              ) {
                window.location.href =
                  res.message.data.attributes.next_action.redirect.url;
                this.isLoading = false;
                this.isPreviewModal = false;
              } else if (res.message.data.attributes.status == "succeeded") {
                this.modeTwoStatus({
                  module: "kanto_ads",
                  intentId: res.message.data.id,
                })
                  .then((res) => {
                    if (
                      res.status == "ok" &&
                      res.message.data.attributes.status == "succeeded"
                    ) {
                      this.isLoading = false;
                      this.isPreviewModal = false;
                      this.isReceiptModal = true;
                      this.paymentDate = moment(
                        res.message.data.logData.updatedAt
                      ).format("ll");
                      this.paymentStatus = res.message.data.logData.status;
                    } else {
                      this.isPreviewModal = false;
                      this.redirectToPayMessage(
                        "failed",
                        this.$route.query.module,
                        this.$route.query.skuAmount,
                        this.$route.query.methodId,
                        this.$route.query.paymentIntentId
                      );
                    }
                  })
                  .catch((err) => {
                    this.isLoading = false;
                    this.isPreviewModal = false;
                    this.redirectToPayMessage(
                      "failed",
                      this.$route.query.module,
                      this.$route.query.skuAmount,
                      this.$route.query.methodId,
                      this.$route.query.paymentIntentId
                    );
                    console.log(err);
                  });
              }
            } else {
              this.isLoading = false;
              this.isPreviewModal = false;
              this.$toast.showToast("danger", "Top-up Failed!", res.message);
            }
          })
          .catch((err) => {
            this.isLoading = false;
            this.isPreviewModal = false;
            this.redirectToPayMessage(
              "failed",
              this.$route.query.module,
              this.$route.query.skuAmount,
              this.$route.query.methodId,
              this.$route.query.paymentIntentId
            );
            console.log(err);
          });
      }
    },
  },
  computed: {
    ...authComputed,
    ...walletComputed,
    selectedBank: {
      get() {
        var opt = {};
        if (this.bankTransferDetails.bankTransferTo) {
          this.banks.forEach((b) => {
            if (b.name == this.bankTransferDetails.bankTransferTo) {
              opt = b;
            }
          });
        }
        return opt;
      },
    },
    bankOptions: {
      get() {
        var options = [{ value: null, text: "Select bank", disabled: true }];

        this.banks.forEach((b) => {
          options.push({
            value: b.name,
            text: b.name,
          });
        });

        return options;
      },
    },
    breadCrumbs() {
      if (this.hasReferrer) {
        return [
          { text: "Checkout", to: { name: "confirm-order" } },
          { text: "Wallet", to: { name: "wallet" } },
          { text: "Topup", to: { name: "wallet-topup" } },
        ];
      } else {
        return [
          { text: "Wallet", to: { name: "wallet" } },
          { text: "Topup", to: { name: "wallet-topup" } },
        ];
      }
    },
    cardMonth() {
      if (this.cardDetails.cardMonth) {
        return moment().month(this.cardDetails.cardMonth).format("MMMM");
      } else {
        return "";
      }
    },
    amountValidation: {
      get() {
        if (this.selectedPm) {
          return "required|min_value:" + this.selectedPm.minAmount;
        } else {
          return "required|min_value:0";
        }
      },
    },
    years: {
      get() {
        var year = moment().year();
        var options = [
          {
            text: year,
            value: year,
          },
        ];

        for (var i = 0; i < 30; i++) {
          year++;
          options.push({
            text: year,
            value: year,
          });
        }

        return options;
      },
    },
    selectedPm: {
      get() {
        var pm = null;
        this.paymentMethodList.forEach((p) => {
          if (p.methodId == this.selectedPmId) {
            pm = p;
          }
        });
        return pm;
      },
    },
    selectedSku: {
      get() {
        var sku = null;
        this.walletSkus.forEach((s) => {
          if (s.amount == this.selectedAmount) {
            sku = s;
          }
        });
        return sku;
      },
    },
    amountToBeReceived: {
      get() {
        return parseInt(this.selectedAmount)
          ? parseInt(this.selectedAmount).toLocaleString()
          : 0;
      },
    },
  },
  components: {
    Layout,
    PageHeader,
    ImageUploader,
  },
  async mounted() {
    await this.fetchPaymentMethods();
    await this.fetchWalletSkus();

    if (this.$route.query.skuAmount) {
      this.selectedAmount = this.$route.query.skuAmount;
    }

    if (this.$route.query.methodId) {
      this.selectedPmId = this.$route.query.methodId;
    }

    if (this.$route.query.referrer) {
      this.hasReferrer = true;
      localStorage.setItem("topupReferrer", this.$route.query.referrer);
      console.log("Referrer:" + localStorage.getItem("topupReferrer"));
    }

    if (this.$route.query.module && this.$route.query.paymentIntentId) {
      if (this.selectedPm.endpointType == "source") {
        console.log("test");
      } else {
        this.modeTwoStatus({
          module: this.$route.query.module,
          intentId: this.$route.query.paymentIntentId,
        })
          .then((res) => {
            if (
              res.status == "ok" &&
              res.message.data.attributes.status == "succeeded"
            ) {
              this.isReceiptModal = true;
              this.paymentDate = moment(
                res.message.data.logData.updatedAt
              ).format("ll");
              this.paymentStatus = res.message.data.logData.status;
            } else {
              this.redirectToPayMessage(
                "failed",
                this.$route.query.module,
                this.$route.query.skuAmount,
                this.$route.query.methodId,
                this.$route.query.paymentIntentId
              );
            }
          })
          .catch((err) => {
            console.log(err);
            this.redirectToPayMessage(
              "failed",
              this.$route.query.module,
              this.$route.query.skuAmount,
              this.$route.query.methodId,
              this.$route.query.paymentIntentId
            );
          });
      }
    }
  },
};
</script>

<style>
.wallet-container {
  max-width: 800px;
}

.sku-item {
  cursor: pointer;
  border: solid 1px #e1e1e1;
  border-radius: 8px;
  padding: 10px;
  font-size: 14px;
  font-weight: 700;
  height: 65px;
}

.sku-item.active {
  background: #f84b04;
  color: #fff;
}

.sku-item.disabled {
  color: #e1e1e1;
  pointer-events: none;
}

.payment-method-item {
  cursor: pointer;
  border: solid 1px #e1e1e1;
  border-radius: 8px;
  padding: 10px;
}

.payment-method-item.active {
  border-color: #f84b04;
}

.pm-logo {
  height: 32px;
}

.pm-text {
  font-size: 10px;
  font-weight: 400;
}

#guide-modal .modal-label,
#transfer-modal .modal-label,
#preview-modal .modal-label,
#receipt-modal .modal-label {
  font-weight: 700;
  font-size: 15px;
}

#guide-modal .modal-title,
#transfer-modal .modal-title,
#preview-modal .modal-title,
#receipt-modal .modal-title {
  font-weight: 500;
  font-size: 24px;
}

#guide-modal .modal-header,
#transfer-modal .modal-header,
#preview-modal .modal-header {
  border-bottom: none;
  margin-bottom: 10px;
}

#guide-modal .modal-header .close,
#transfer-modal .modal-header .close,
#preview-modal .modal-header .close {
  opacity: 1;
}

#guide-modal .modal-footer,
#transfer-modal .modal-footer,
#preview-modal .modal-footer {
  border-top: none;
}


#transfer-modal .modal-dialog,
#preview-modal .modal-dialog {
  width: 450px;
}

#guide-modal .modal-dialog {
  width: 557px;
}

.section-title {
  font-size: 16px;
  font-weight: 700;
}

.next-button {
  width: 150px;
}

#receipt-modal .modal-dialog {
  width: 450px;
}

#receipt-modal .modal-header {
  border-bottom: none;
  margin-bottom: 10px;
}

#receipt-modal .modal-header .close {
  opacity: 1;
}

#receipt-modal .modal-footer {
  border-top: none;
}

.hr-broken {
  border-top: 1px dashed #4b4b4b;
}

.thank-you {
  font-size: 14px;
  font-weight: 500;
}

.receipt-text {
  font-size: 11px;
  font-weight: 400;
}

.green {
  color: #508f30;
}

.payment-status {
  text-transform: capitalize;
}

.red {
  color: #ff0000;
}

.pm-details {
  color: #6f6f6f;
}
</style>